import { inject, injectable } from 'inversify'
import type { INotificationSoundService } from '@/services/types'
import type LoggerService from '@/services/loggerService'
import { AWS_S3_URL } from '@/constants/constants'
import { SERVICE_TYPES } from '@/core/container/types'

import ButtonTinyAudioUrl from '@/assets/sounds/button_tiny.mp3'
import type WindowService from '@/services/browser/windowService'
import type { MonitoringServiceInterface } from '@/services/monitoring/types'
import { LogLevel } from '@/core/logger/types'

const NOT_ALLOWED_ERROR = 'NotAllowedError'

type AudioBufferCache = {
  binaryArrayBuffer?: ArrayBuffer
  decodedAudioBuffer?: AudioBuffer
  contextId?: number
}

@injectable()
export default class NotificationSoundService implements INotificationSoundService {
  // Web Audio API properties
  protected failedAudioBuffer: AudioBuffer | null = null

  protected failedAudioVolume: number | null = null

  protected audioContext: AudioContext | null = null

  protected audioBufferCache = new Map<string, AudioBufferCache>()

  protected contextIdCounter = 0

  protected gainNode: GainNode | null = null

  private isAudioInitialized = false

  private currentRetryHandler: (() => void) | null = null

  private currentSetupHandler: (() => void) | null = null

  // Events for first user interaction
  private readonly userInteractionEvents: Array<keyof WindowEventMap> = ['click', 'touchstart', 'keydown']

  constructor(
    @inject(SERVICE_TYPES.LoggerService) protected readonly loggerService: LoggerService,
    @inject(SERVICE_TYPES.MonitoringService) protected readonly monitoringService: MonitoringServiceInterface,
    @inject(SERVICE_TYPES.WindowService) protected readonly windowService: WindowService,
  ) {}

  /**
   * Initialize audio system for notifications
   * Should be called early in the application lifecycle
   * This method is idempotent and can be called multiple times safely
   */
  public async initAudio(): Promise<void> {
    if (this.isAudioInitialized) return

    this.setupAudioActivationHandlers()
    this.setupVisibilityHandlers()
    this.isAudioInitialized = true
  }

  /**
   * Early prepare audio context for user interaction
   */
  private setupAudioActivationHandlers(): void {
    const prepareAudioHandler = async () => {
      try {
        await this.initAudioContext()
        if (!this.failedAudioBuffer) {
          await this.playSilentSound()
        }
        await this.preDecodeAudioFiles()
      } catch (error) {
        this.loggerService.error('error', `Failed to prepare audio context: ${(error as Error).message}`)
      }
    }

    const onceHandler = () => {
      prepareAudioHandler()
      this.clearSetupHandler()
    }

    this.currentSetupHandler = onceHandler
    this.userInteractionEvents.forEach((eventName) =>
      this.windowService.self().addEventListener(eventName, onceHandler, true),
    )
  }

  private visibilityHandler = async () => {
    if (!document.hidden && this.audioContext) {
      if (this.audioContext.state === 'suspended') {
        // Edge/Chrome workaround: AudioContext.resume() is not working immediately after visibilitychange
        setTimeout(async () => {
          try {
            if (this.audioContext?.state === 'suspended') {
              await this.tryResumeAudioContext()
            }
          } catch (error) {
            this.loggerService.error('error', `Failed to resume AudioContext: ${(error as Error).message}`)
          }
        }, 200)
      } else if (this.audioContext.state === 'closed') {
        try {
          await this.resetAudioContext()
        } catch (error) {
          this.loggerService.error('error', `Failed to reset AudioContext: ${(error as Error).message}`)
        }
      }
    }
  }

  /**
   * Resume AudioContext after activation tabs from background
   */
  private setupVisibilityHandlers(): void {
    document.addEventListener('visibilitychange', this.visibilityHandler)
  }

  private isAudioContextValid(): boolean {
    return Boolean(this.audioContext && this.audioContext.state !== 'closed')
  }

  // Avoid Sentry noise
  private isAutoplayPolicyDeny(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false
    const { name, message } = error as Record<string, unknown>

    if (name === NOT_ALLOWED_ERROR) return true

    if (typeof message === 'string') {
      return /play\(\)\s+failed/i.test(message)
    }

    return false
  }

  public async resetAudioContext(): Promise<void> {
    try {
      if (this.audioContext && this.audioContext.state !== 'closed') {
        await this.audioContext.close()
      }

      this.audioContext = null
      this.gainNode = null
      this.clearAudioCache()

      await this.initAudioContext()
    } catch (error) {
      this.loggerService.error('error', `Failed to reset AudioContext: ${(error as Error).message}`)
      throw error
    }
  }

  private async initAudioContext(): Promise<void> {
    try {
      if (!this.audioContext) {
        this.audioContext = new AudioContext()
        this.contextIdCounter += 1
        this.clearAudioCache()
      }

      if (this.audioContext?.state === 'closed') {
        this.audioContext = null
        this.gainNode = null
        this.contextIdCounter += 1
        this.clearAudioCache()
      }

      if (!this.gainNode && this.audioContext) {
        this.gainNode = this.audioContext.createGain()
        this.gainNode.connect(this.audioContext.destination)
      }

      if (!this.audioContext || !this.gainNode) {
        throw new Error('Failed to initialize audio context or gain node')
      }

      if (this.audioContext?.state === 'suspended') {
        await this.tryResumeAudioContext()
      }
    } catch (error) {
      if (!this.isAutoplayPolicyDeny(error)) {
        this.monitoringService.logInfo(`Failed to initialize AudioContext: ${(error as Error).message}`, LogLevel.ERROR)
      }

      if (this.audioContext?.state === 'closed') {
        await this.cleanUp()
      }
      throw error
    }
  }

  /**
   * AudioContext.resume() can stack in some browsers or cases
   */
  private async tryResumeAudioContext(): Promise<void> {
    await Promise.race([
      this.audioContext?.resume(),
      new Promise((_resolve, reject) => {
        setTimeout(() => {
          const error = new Error('User interaction required for audio playback')
          error.name = NOT_ALLOWED_ERROR
          reject(error)
        }, 1000)
      }),
    ])
  }

  private async playWebAudio(audioBuffer: AudioBuffer, volume = 0.5): Promise<void> {
    if (!this.isAudioContextValid()) {
      await this.initAudioContext()
    }

    if (this.audioContext?.state === 'suspended') {
      const error = new Error('User interaction required for audio playback')
      error.name = NOT_ALLOWED_ERROR
      throw error
    }

    if (this.audioContext?.state === 'closed') {
      await this.resetAudioContext()
    }

    if (!audioBuffer || !this.gainNode || !this.audioContext) {
      throw new Error('Audio not properly initialized')
    }

    const source = this.audioContext.createBufferSource()
    source.buffer = audioBuffer

    this.gainNode.gain.value = Math.max(0, Math.min(1, volume))
    source.connect(this.gainNode)
    source.start(0)

    source.onended = () => {
      source.disconnect()
    }
  }

  /**
   * Retry audio playback after user activation (used in handlePlayError)
   */
  private async retryAudioPlaybackByClick(audioBuffer: AudioBuffer, volume = 0.5): Promise<void> {
    if (!this.isAudioContextValid()) {
      await this.initAudioContext()
    }

    if (this.audioContext?.state === 'suspended') {
      await this.tryResumeAudioContext()
    }

    if (this.audioContext?.state === 'closed') {
      await this.resetAudioContext()
    }

    if (!audioBuffer || !this.gainNode || !this.audioContext) {
      throw new Error('Audio not properly initialized')
    }

    const source = this.audioContext.createBufferSource()
    source.buffer = audioBuffer

    this.gainNode.gain.value = Math.max(0, Math.min(1, volume))
    source.connect(this.gainNode)
    source.start(0)

    source.onended = () => {
      source.disconnect()
    }
  }

  private getAudioUrl(path?: string): string {
    return path ? AWS_S3_URL + path : ButtonTinyAudioUrl
  }

  /**
   * Smart cache clearing - preserve arrayBuffer, clear only audioBuffer parts
   * Called when AudioContext changes/recreates
   */
  private clearAudioCache(): void {
    for (const [url, cached] of this.audioBufferCache.entries()) {
      if (cached.binaryArrayBuffer) {
        cached.decodedAudioBuffer = undefined
        cached.contextId = undefined
      } else {
        this.audioBufferCache.delete(url)
      }
    }
  }

  /**
   * Load audio file into cache.
   * If audio context is initialized, decode it.
   * Else cache only arrayBuffer without decoding.
   */
  public async preloadAudioFile(path?: string): Promise<void> {
    try {
      const url = this.getAudioUrl(path)

      if (this.audioBufferCache.has(url)) {
        return
      }

      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`Failed to fetch audio file: ${response.status} ${response.statusText}`)
      }

      const binaryArrayBuffer = await response.arrayBuffer()

      if (this.isAudioContextValid() && this.audioContext?.state !== 'suspended') {
        const decodedAudioBuffer = await this.audioContext!.decodeAudioData(binaryArrayBuffer)
        this.audioBufferCache.set(url, {
          decodedAudioBuffer,
          contextId: this.contextIdCounter,
        })
      } else {
        this.audioBufferCache.set(url, {
          binaryArrayBuffer,
        })
      }
    } catch (error) {
      this.loggerService.error('error', `Failed to load audio file: ${(error as Error).message}`)
      throw error
    }
  }

  /**
   * Get or decode AudioBuffer from cache
   * Requires initialized AudioContext
   */
  private async getCachedAudioBuffer(path?: string): Promise<AudioBuffer> {
    const url = this.getAudioUrl(path)

    if (!this.isAudioContextValid()) {
      await this.initAudioContext()
    }

    const cached = this.audioBufferCache.get(url)

    if (!cached || (!cached.decodedAudioBuffer && !cached.binaryArrayBuffer)) {
      throw new Error(`Audio file not loaded: ${url}. Call preloadAudioFile first.`)
    }

    if (cached.decodedAudioBuffer && cached.contextId === this.contextIdCounter) {
      return cached.decodedAudioBuffer
    }

    if (!this.audioContext) {
      throw new Error('AudioContext not initialized')
    }

    if (!cached.binaryArrayBuffer) {
      throw new Error(`Audio file not loaded: ${url}. ArrayBuffer is empty`)
    }

    // Clone ArrayBuffer before decoding, because decodeAudioData makes it detached
    const clonedBuffer = cached.binaryArrayBuffer.slice(0)
    const audioBuffer = await this.audioContext.decodeAudioData(clonedBuffer)

    cached.decodedAudioBuffer = audioBuffer
    cached.contextId = this.contextIdCounter
    cached.binaryArrayBuffer = undefined // Free memory, browser will cache the file

    return audioBuffer
  }

  /**
   * Pre-decode cached audio files for better performance
   * Call this after user interaction to optimize future playback
   */
  public async preDecodeAudioFiles(): Promise<void> {
    if (!this.isAudioContextValid()) {
      return
    }

    const decodePromises: Promise<void>[] = []

    for (const [url, cached] of this.audioBufferCache.entries()) {
      // Skip if already decoded for current context
      if (cached.decodedAudioBuffer && cached.contextId === this.contextIdCounter) {
        continue
      }

      const path = url.startsWith(AWS_S3_URL) ? url.substring(AWS_S3_URL.length) : undefined
      const decodePromise = this.getCachedAudioBuffer(path)
        .then(() => {
          // Convert to void
        })
        .catch((error) => {
          this.loggerService.error('error', `Failed to pre-decode ${url}: ${(error as Error).message}`)
        })

      decodePromises.push(decodePromise)
    }

    await Promise.allSettled(decodePromises)
  }

  public async playSound(path?: string, volume?: number): Promise<void> {
    let audioBuffer: AudioBuffer | undefined
    try {
      await this.preloadAudioFile(path)
      audioBuffer = await this.getCachedAudioBuffer(path)
      await this.playWebAudio(audioBuffer, volume)
    } catch (error) {
      this.handlePlayError(error as Error, audioBuffer, volume, path)
    }
  }

  // Workaround for background tabs.
  // Play silent sound after user interaction for activate long audio context.
  private playSilentSound() {
    if (!this.isAudioContextValid()) {
      this.loggerService.error('error', 'AudioContext not initialized. Need user interaction first.')
      return
    }

    const ctx = this.audioContext!
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = 'sine'
    osc.frequency.value = 110
    gain.gain.value = 0.001 // Volume. Don't use less than 0.001, because browser didn't play it
    const soundDuration = 0.05

    osc.connect(gain).connect(ctx.destination)

    osc.start()
    osc.stop(ctx.currentTime + soundDuration)
    osc.onended = () => {
      osc.disconnect()
      gain.disconnect()
    }
  }

  public async cleanUp(): Promise<void> {
    try {
      this.isAudioInitialized = false
      this.failedAudioBuffer = null
      this.failedAudioVolume = null

      this.clearRetryHandler()
      this.clearSetupHandler()

      if (this.audioContext && this.audioContext.state !== 'closed') {
        await this.audioContext.close()
      }

      this.audioContext = null
      this.gainNode = null
      this.audioBufferCache.clear()
      this.contextIdCounter += 1

      document.removeEventListener('visibilitychange', this.visibilityHandler)
    } catch (error) {
      this.monitoringService.logInfo(`Failed to cleanup audio resources: ${(error as Error).message}`, LogLevel.ERROR)
    }
  }

  /**
   * Handle audio playback errors and setup retry mechanism for NotAllowedError
   */
  private handlePlayError(error: Error, audioBuffer?: AudioBuffer, volume = 0.5, path?: string): void {
    if (error.name === NOT_ALLOWED_ERROR) {
      this.clearRetryHandler()
      this.clearSetupHandler() // Avoid conflict with setup handler

      // Save parameters for retry after user interaction
      if (audioBuffer) {
        this.failedAudioBuffer = audioBuffer
      }
      this.failedAudioVolume = volume
      const failedPath = path

      const playOnActivate = async () => {
        try {
          if (this.failedAudioBuffer) {
            const buffer = this.failedAudioBuffer
            const vol = this.failedAudioVolume ?? 0.5

            this.failedAudioBuffer = null
            this.failedAudioVolume = null

            await this.retryAudioPlaybackByClick(buffer, vol)
          } else if (failedPath) {
            await this.preloadAudioFile(failedPath)
            const buffer = await this.getCachedAudioBuffer(failedPath)
            await this.retryAudioPlaybackByClick(buffer, volume)
          } else {
            // For default sound (path = undefined)
            await this.preloadAudioFile()
            const buffer = await this.getCachedAudioBuffer()
            await this.retryAudioPlaybackByClick(buffer, volume)
          }

          this.performDelayedInitialization()
        } catch (retryError) {
          const msg = `Failed to retry audio playback: ${(retryError as Error).message}, path=${failedPath}`
          if (this.isAutoplayPolicyDeny(retryError)) {
            this.loggerService.log('error', msg)
          } else {
            this.monitoringService.logInfo(msg, LogLevel.ERROR)
          }
        }

        this.clearRetryHandler()
      }

      this.currentRetryHandler = playOnActivate
      this.userInteractionEvents.forEach((eventName) =>
        this.windowService.self().addEventListener(eventName, playOnActivate, true),
      )
    } else {
      const msg = `Audio playback error: ${(error as Error).message}, path=${path}`
      this.monitoringService.logInfo(msg, LogLevel.ERROR)
    }
  }

  private clearRetryHandler(): void {
    if (this.currentRetryHandler) {
      this.userInteractionEvents.forEach((eventName) =>
        this.windowService.self().removeEventListener(eventName, this.currentRetryHandler!, true),
      )
      this.currentRetryHandler = null
    }
  }

  private clearSetupHandler(): void {
    if (this.currentSetupHandler) {
      this.userInteractionEvents.forEach((eventName) =>
        this.windowService.self().removeEventListener(eventName, this.currentSetupHandler!, true),
      )
      this.currentSetupHandler = null
    }
  }

  private performDelayedInitialization(): void {
    this.preDecodeAudioFiles().catch((error) => {
      this.loggerService.error('error', `Failed to pre-decode files during delayed init: ${(error as Error).message}`)
    })
  }
}
